import * as yup from 'yup';

import { sharedBillingValidationSchema, sharedBillingValidationSchemaV2 } from '../SharedBilling';

import { contactsSchema } from './sections/ContactsSection/schema';

const REQUIRED_DETAIL_SUPPORT_TYPES = ['allergy_risk', 'behavior_support'];

const riderFormProfileSchema = (shouldValidateInvoiceGroup: boolean) =>
  yup.object({
    firstName: yup.string().required('Please enter a first name'),
    lastName: yup.string().required('Please enter a last name'),
    gender: yup.string().required('Please enter a gender'),
    category: yup.string().nullable().required('Please enter a category'),
    mobilePhoneNumber: yup
      .string()
      .test('valid-phone-number', '*Incorrect formatting', (p) => (p ? /^[0-9]{10}$/.test(p) : true))
      .test('start-with-zero', 'Phone number can not begin with 0', (p) => !(p && p[0] === '0')),
    email: yup.string().email().nullable(),
    invoiceGroupId: shouldValidateInvoiceGroup
      ? yup.number().nullable().required('Please enter an invoice group')
      : yup.number().nullable(),
    photosAttributes: yup.array().of(
      yup.object({
        attachment: yup.mixed().test('fileSize', 'Photo size is greater than 10MB.', (value) => {
          if (!value?.length) {
            return true;
          }

          return value[0].size <= 10000000;
        }),
      })
    ),
  });

export const validationSchema = yup.object({
  codeWord: yup.string().trim().required('Code word is required'),
  frontSeatAuthorized: yup.boolean().required(),
  boosterSeat: yup.boolean().required(),
  dateOfBirth: yup
    .date()
    .nullable()
    .required('Please enter a valid date of birth')
    .typeError('Please enter a valid date of birth'),
  services: yup.array().of(yup.string().required()).required(),
  supportSettings: yup
    .array()
    .of(
      yup.object({
        supportType: yup.string().required(),
        details: yup.string().when(['supportType', 'selected'], {
          is: (supportType: string, selected: boolean) =>
            selected && REQUIRED_DETAIL_SUPPORT_TYPES.includes(supportType),
          then: (schema) => schema.trim().min(1, 'Required field'),
          otherwise: (schema) => schema.defined(),
        }),
        selected: yup.boolean().required(),
      })
    )
    .required(),
  riderNotes: yup.string().max(500, 'Maximum 500 characters').defined(),
});

export const buildValidationSchema = (options: { validateInvoiceGroup: boolean; useApprovalFlowV2: boolean }) => {
  const profileSchema = riderFormProfileSchema(options.validateInvoiceGroup);
  const billingSchema = options.useApprovalFlowV2 ? sharedBillingValidationSchemaV2() : sharedBillingValidationSchema();
  const contactsYupSchema = yup.object({ contacts: contactsSchema });

  return profileSchema.concat(billingSchema).concat(contactsYupSchema).concat(validationSchema);
};
